import { createIntl, createIntlCache } from 'react-intl'
import enFrontend from 'i18n/frontend.en.json'
import esFrontend from 'i18n/frontend.es.json'
import thFrontend from 'i18n/frontend.th.json'
import ptFrontend from 'i18n/frontend.pt.json'
import ruFrontend from 'i18n/frontend.ru.json'
import ukFrontend from 'i18n/frontend.uk.json'
import noFrontend from 'i18n/frontend.no.json'
import plFrontend from 'i18n/frontend.pl.json'
import enLogbook from 'i18n/logbook.en.json'
import esLogbook from 'i18n/logbook.es.json'
import translations from 'i18n/translations'

export const defaultLang = 'en'
export const defaultTranslates = 'en'

export const messages = {
  en: { ...enFrontend, ...enLogbook },
  es: { ...esFrontend, ...esLogbook },
  th: { ...thFrontend, ...enLogbook },
  pt: { ...ptFrontend, ...enLogbook },
  ru: { ...ruFrontend, ...enLogbook },
  uk: { ...ukFrontend, ...enLogbook },
  no: { ...noFrontend, ...enLogbook },
  pl: { ...plFrontend, ...enLogbook },
}

export const getCurrentLang = () => localStorage.getItem('lang') || defaultLang

export const getCurrentMessages = () => messages[getCurrentLang()]

export const onError = e => {
  console.warn(
    `Translation for key "${e.descriptor.id}" is missing from the current dictionary`,
  )
}

let intl = createIntl(
  {
    onError,
    locale: getCurrentLang(),
    messages: messages[getCurrentLang()],
  },
  createIntlCache(),
)

export const getIntl = () => intl

export const setIntl = locale => {
  localStorage.setItem(
    'lang',
    messages.hasOwnProperty(locale) ? locale : defaultLang,
  )
  intl = createIntl(
    {
      onError,
      locale: getCurrentLang(),
      messages: messages[getCurrentLang()],
    },
    createIntlCache(),
  )
}

/**
 * https://formatjs.io/docs/intl#formatmessage
 * @param descriptor: String | MessageDescriptor: {
 *    id: string
 *    defaultMessage?: string
 *    description?: string | object
 *  },
 * @param values
 * @returns string | React.ReactNodeArray
 */
export const formatMessage = (descriptor, values) => {
  if (typeof descriptor === 'string') {
    // eslint-disable-next-line no-param-reassign
    descriptor = translations.hasOwnProperty(descriptor)
      ? translations[descriptor]
      : { id: descriptor }
  }

  return getIntl().formatMessage(descriptor, values)
}

/**
 * https://formatjs.io/docs/intl#formattime
 * @param value: number | Date,
 * @param options?: Intl.DateTimeFormatOptions & {format?: string}
 * @returns string
 */
export const formatTime = (value, options) =>
  getIntl().formatTime(value, options)

/**
 * https://formatjs.io/docs/intl#formatdate
 * @param value: number | Date
 * @param options?: Intl.DateTimeFormatOptions & {format?: string}
 * @returns string
 */
export const formatDate = (value, options) =>
  getIntl().formatDate(value, options)

/**
 * https://formatjs.io/docs/intl#formatrelativetime
 * @param value: number,
 * @param unit: Unit,
 * @param options?: Intl.RelativeTimeFormatOptions & { format?: string }
 * @returns string
 */
export const formatRelativeTime = (value, unit, options) =>
  getIntl().formatRelativeTime(value, unit, options)

/**
 * https://formatjs.io/docs/intl#formatnumber
 * @param value: number,
 * @param options?: Intl.NumberFormatOptions & {format?: string}
 * @returns string
 */
export const formatNumber = (value, options) =>
  getIntl().formatNumber(value, options)

/**
 * https://formatjs.io/docs/intl#formatplural
 * @param value: number,
 * @param options?: Intl.PluralFormatOptions
 * @returns 'zero' | 'one' | 'two' | 'few' | 'many' | 'other'
 */
export const formatPlural = (value, options) =>
  getIntl().formatPlural(value, options)

/**
 * https://formatjs.io/docs/intl#formatlist
 * @param elements: (string | React.ReactNode)[],
 * @param options?: Intl.ListFormatOptions
 * @returns string | React.ReactNode[]
 */
export const formatList = (elements, options) =>
  getIntl().formatList(elements, options)

/**
 * https://formatjs.io/docs/intl#formatdisplayname
 * @param value: string | number | Record<string, unknown>,
 * @param options: FormatDisplayNameOptions
 * @returns string | undefined
 */
export const formatDisplayName = (value, options) =>
  getIntl().formatDisplayName(value, options)
